import { useTranslations } from '@vocab/react';
import { Dialog, Text, Button, Inline } from 'braid-design-system';

import errorTranslations from 'src/core/domain/errors/.vocab';
import { DeleteError } from 'src/core/domain/errors/deleteErrors';
import type { InteractionType } from 'src/provider/optionDialog';
import {
  deleteComment,
  deleteReply,
  deleteThread,
} from 'src/services/threadInteractionService';

import translations from './.vocab';

import './DeleteDialog.css';

interface DeleteDialogProps {
  id: string;
  contentId: string;
  interaction: InteractionType;
  open: boolean;
  onClose: () => void;
  onDeleteSubmit?: () => void;
  onDeleteSuccess: (message: string) => void;
  onDeleteError: (message: string) => void;
}

export default ({
  id,
  contentId,
  interaction,
  open,
  onClose,
  onDeleteSubmit,
  onDeleteSuccess,
  onDeleteError,
}: DeleteDialogProps) => {
  const { t } = useTranslations(translations);
  const { t: errorTranslation } = useTranslations(errorTranslations);

  const handleDeleteError = (err: unknown) => {
    const deleteError = err instanceof DeleteError ? err : new DeleteError();
    const message = errorTranslation(deleteError.key);
    onDeleteError(message);
  };

  const handleThreadDelete = async () => {
    try {
      await deleteThread({ id: contentId });
      onDeleteSuccess(t('threadIsDeleted'));
    } catch (err) {
      handleDeleteError(err);
    }
  };
  const handleCommentDelete = async () => {
    try {
      await deleteComment({ commentId: contentId });
      onDeleteSuccess(t('commentIsDeleted'));
    } catch (err) {
      handleDeleteError(err);
    }
  };

  const handleReplyDelete = async () => {
    try {
      await deleteReply({ replyId: contentId });
      onDeleteSuccess(t('commentIsDeleted'));
    } catch (err) {
      handleDeleteError(err);
    }
  };

  const handleDeleteSubmit = async () => {
    onDeleteSubmit?.();
    switch (interaction) {
      case 'thread':
        await handleThreadDelete();
        break;
      case 'comment':
        await handleCommentDelete();
        break;
      case 'reply':
        await handleReplyDelete();
        break;
      default:
        break;
    }
    onClose();
  };

  return (
    <Dialog
      id={id}
      title={
        interaction === 'thread'
          ? t('deleteThreadTitle')
          : t('deleteCommentTitle')
      }
      description={
        <Text tone="secondary">
          {interaction === 'thread'
            ? t('deleteThreadDescription')
            : t('deleteCommentDescription')}
        </Text>
      }
      open={open}
      onClose={onClose}
      width="small"
      closeLabel={t('cancelButton')}
    >
      <Inline space="small" align="left">
        <Button variant="ghost" tone="formAccent" onClick={handleDeleteSubmit}>
          {t('deleteButton')}
        </Button>
        <Button variant="transparent" tone="formAccent" onClick={onClose}>
          {t('cancelButton')}
        </Button>
      </Inline>
    </Dialog>
  );
};
